import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocationServices } from '../useLocationServices';
import {
  includeGeocoderResult,
  isWithinServiceBounds,
} from '../useAutocompleteService/useAutocompleteService';
import type {
  GeolocationConfig,
  GeolocationResult,
  GeolocationCoordinatesWithAddress,
  GeolocationPositionWithMetadata,
} from './types';

const isLocationSupported = (
  result: google.maps.GeocoderResult,
  coords: GeolocationCoordinatesWithAddress
) => {
  return (
    includeGeocoderResult(result) === true ||
    isWithinServiceBounds({
      lat: coords.latitude,
      lng: coords.longitude,
    })
  );
};

/**
 * Hook abstracting away the interaction with the Geolocation API.
 * @param config - the configuration to use
 */
export function useGeolocation(
  config: GeolocationConfig = {}
): GeolocationResult {
  const {
    positionOptions = {
      enableHighAccuracy: false,
      maximumAge: 0,
      timeout: Infinity,
    },
    isOptimisticGeolocationEnabled = true,
    userDecisionTimeout = undefined,
    suppressLocationOnMount = false,
    watchPosition = false,
    geolocationProvider = typeof navigator !== 'undefined'
      ? navigator.geolocation
      : undefined,
    watchLocationPermissionChange = true,
    onError,
    onSuccess,
  } = config;

  const { geocoder, isReady } = useLocationServices();

  const userDecisionTimeoutId = useRef(0);
  const isCurrentlyMounted = useRef(true);
  const watchId = useRef<number>(0);

  const [isGeolocationEnabled, setIsGeolocationEnabled] = useState(
    isOptimisticGeolocationEnabled
  );

  const [permissionState, setPermissionState] = useState<
    PermissionState | undefined
  >();

  const reverseGeocode = useCallback(
    (position: GeolocationPosition) => {
      const enhancedPosition: GeolocationPositionWithMetadata = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
      geocoder
        ?.geocode({
          location: {
            lat: enhancedPosition.latitude,
            lng: enhancedPosition.longitude,
          },
        })
        .then((response) => {
          if (response.results.length === 0) {
            return;
          }
          enhancedPosition.formattedAddress =
            response.results[0].formatted_address;
          enhancedPosition.isWithinSupportedArea = isLocationSupported(
            response.results[0],
            position.coords
          );
        })
        .catch((error) => {
          enhancedPosition.isWithinSupportedArea = isWithinServiceBounds({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });

          onError?.(error);
        })
        .finally(() => {
          if (isCurrentlyMounted.current) {
            setIsGeolocationEnabled(true);
          }
          // dispatch set location
          onSuccess?.(enhancedPosition);
        });
    },
    [geocoder, onSuccess, onError]
  );

  const cancelUserDecisionTimeout = useCallback(() => {
    if (userDecisionTimeoutId.current) {
      window.clearTimeout(userDecisionTimeoutId.current);
    }
  }, []);

  const handlePositionError = useCallback(
    (error?: GeolocationPositionError) => {
      cancelUserDecisionTimeout();
      if (isCurrentlyMounted.current) {
        setIsGeolocationEnabled(false);
      }
      onError?.(error);
    },
    [onError, cancelUserDecisionTimeout]
  );

  const handlePositionSuccess = useCallback(
    (position: GeolocationPosition) => {
      cancelUserDecisionTimeout();
      reverseGeocode(position);
    },
    [cancelUserDecisionTimeout, reverseGeocode]
  );

  const getPosition = useCallback(() => {
    if (
      !geolocationProvider?.getCurrentPosition ||
      // we really want to check if the watchPosition is available
      !geolocationProvider.watchPosition
    ) {
      return; // Silently return if no geolocation provider is available
    }

    if (userDecisionTimeout && permissionState !== 'granted') {
      userDecisionTimeoutId.current = window.setTimeout(() => {
        handlePositionError();
      }, userDecisionTimeout);
    }

    if (watchPosition) {
      watchId.current = geolocationProvider.watchPosition(
        handlePositionSuccess,
        handlePositionError,
        positionOptions
      );
    } else {
      geolocationProvider.getCurrentPosition(
        handlePositionSuccess,
        handlePositionError,
        positionOptions
      );
    }
  }, [
    geolocationProvider,
    watchPosition,
    userDecisionTimeout,
    handlePositionError,
    handlePositionSuccess,
    positionOptions,
    permissionState,
  ]);

  useEffect(() => {
    let permission: PermissionStatus | undefined = undefined;

    if (
      watchLocationPermissionChange &&
      geolocationProvider &&
      'permissions' in navigator
    ) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          permission = result;
          permission.onchange = () => {
            if (permission) {
              setPermissionState(permission.state);
            }
          };
        })
        .catch((e: unknown) => {
          // eslint-disable-next-line no-console
          console.error('❗Error updating the permissions', e);
        });
    }

    return () => {
      if (permission) {
        permission.onchange = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!suppressLocationOnMount) {
      getPosition();
    }

    return () => {
      cancelUserDecisionTimeout();
      if (watchPosition && watchId.current) {
        geolocationProvider?.clearWatch(watchId.current);
      }
    };
  }, [permissionState, isReady]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    getPosition,
    isGeolocationEnabled,
    isGeolocationAvailable: Boolean(geolocationProvider),
  };
}
